import {createAsyncThunk} from '@reduxjs/toolkit';

import {REPORT_CONFIGURATION_SLICE} from '../report-configuration-constants';
import {IReportConfigurationAttribute, TReportConfigurationDataConverted} from '../report-configuration-types';
import {getHeaderAttributes, getTableAttributes} from '../report-dependencies';
import {
    TDeleteTableReportConfigurationArgs,
    deleteTableReportConfiguration,
    getTableReportConfigurationData,
    patchTableReportConfiguration,
    postTableReportConfiguration,
} from './report-configuration-api';
import {convertConfigurationDataForRequest, convertConfigurationDataFromResponse} from './report-configuration-utils';

interface ILoadTableReportConfiguration {
    reportConfigurationConvertedData: TReportConfigurationDataConverted;
    tableAttributes: IReportConfigurationAttribute[];
    headerAttributes: IReportConfigurationAttribute[];
}
export const loadTableReportConfiguration = createAsyncThunk<
    ILoadTableReportConfiguration, {templateCode: string}>(
        `${REPORT_CONFIGURATION_SLICE}/loadTableReportConfigurationData`,
        async ({templateCode}, {rejectWithValue}) => {
            try {
                const [
                    templateConfigurationData,
                    tableAttributes,
                    headerAttributes,
                ] = await Promise.all([
                    getTableReportConfigurationData({templateCode}),
                    getTableAttributes({templateCode}),
                    getHeaderAttributes({templateCode}),
                ]);

                const configurationDataConverted = convertConfigurationDataFromResponse({
                    dataToConvert: templateConfigurationData,
                });

                return {
                    reportConfigurationConvertedData: configurationDataConverted as TReportConfigurationDataConverted,
                    tableAttributes: tableAttributes.data,
                    headerAttributes,
                };
            } catch (e) {
                return rejectWithValue(e);
            }
        },
    );

// Для обновления используется один общий thunk. Если нужны экстра-редьюсеры
// для обновления какой-то конкретной вкладки, то можно создать ещё один аналогичный thunk
export const updateTableReportConfiguration = createAsyncThunk<
    TReportConfigurationDataConverted, {
        templateCode: string;
        data: TReportConfigurationDataConverted;
    }>(
        `${REPORT_CONFIGURATION_SLICE}/updateTableReportConfiguration`,
        async ({templateCode, data}, {rejectWithValue}) => {
            try {
                const dataToSend = convertConfigurationDataForRequest({convertedData: data});
                await patchTableReportConfiguration({
                    templateCode,
                    data: dataToSend,
                });
                return data;
            } catch (e) {
                return rejectWithValue(e);
            }
        },
    );

export const createTableReportConfiguration = createAsyncThunk<
    TReportConfigurationDataConverted, {
        data: TReportConfigurationDataConverted;
    }>(
        `${REPORT_CONFIGURATION_SLICE}/createTableReportConfiguration`,
        async ({data}, {rejectWithValue}) => {
            try {
                const dataToSend = convertConfigurationDataForRequest({convertedData: data});
                await postTableReportConfiguration({
                    data: {
                        ...dataToSend,
                    },
                });
                return data;
            } catch (e) {
                return rejectWithValue(e);
            }
        },
    );

export const removeTableReportConfiguration = createAsyncThunk<
    string, TDeleteTableReportConfigurationArgs>(
        `${REPORT_CONFIGURATION_SLICE}/removeTableReportConfiguration`,
        async (args, {rejectWithValue}) => {
            try {
                const {data} = await deleteTableReportConfiguration(args);
                return data;
            } catch (e) {
                return rejectWithValue(e);
            }
        },
    );
